// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import Decimal from 'decimal.js';
import Axios from 'axios';
import appSettings from '../config/settings';
import { observeVersionHeader } from './appVersion';
import { types as sdkTypes, transit } from './sdkLoader';

export const apiBaseUrl = (marketplaceRootURL) => {
  const port = import.meta.env.VITE_DEV_API_SERVER_PORT;
  const useDevApiServer = import.meta.env.MODE === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return marketplaceRootURL?.replace(/\/$/, '') ?? '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: (v) => new sdkTypes.BigDecimal(v.toString()),
    reader: (v) => new Decimal(v.value),
  },
];

const serialize = (data) => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = (str) => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

const parseResponseData = (response) => {
  const { data, headers } = response;
  const contentType = headers['Content-Type'] ?? headers['content-type'];
  if (contentType?.includes('application/transit+json')) {
    return deserialize(data);
  }
  if (contentType?.includes('application/json')) {
    return JSON.parse(data);
  }
  return data;
};

const axios = Axios.create({
  baseURL: apiBaseUrl(import.meta.env.VITE_MARKETPLACE_ROOT_URL),
});

axios.interceptors.response.use(
  (response) => {
    observeVersionHeader(response.headers, response.config?.method);
    const data = parseResponseData(response);
    response.data = data;
    return response;
  },
  (error) => {
    if (error.response) observeVersionHeader(error.response.headers, error.config?.method);

    // If error is AxiosError, we can parse the response data
    const data = error.response ? parseResponseData(error.response) : {};

    // v2 errors are `{ error: { code, message } }` with the status only on the response;
    // legacy ones carry `status` in the body. `storableError` and ListingPage's 404/403
    // branches read both off the error, so fill them in when the body doesn't.
    const message = typeof data.error === 'object' ? data.error?.message : data.error;
    let e = new Error(message || data.message || error.message || '');
    e = Object.assign(e, data);
    if (e.status == null && error.response) e.status = error.response.status;

    throw e;
  }
);

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
export const request = async (path, options = {}) => {
  const {
    credentials = 'include',
    headers = { 'Content-Type': 'application/transit+json' },
    body,
    method,
    ...rest
  } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const isTransit = headers['Content-Type'] === 'application/transit+json';
  const bodyMaybe = body ? { data: isTransit ? serialize(body) : body } : {};

  const response = await axios.request({
    url: path,
    method,
    headers,
    withCredentials: credentials === 'include',
    responseType: 'text',
    ...bodyMaybe,
    ...rest,
  });

  return response.data;
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = (body) => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = (body) => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = (body) => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = (body) => {
  return post('/api/auth/create-user-with-idp', body);
};

export const getIdBySlug = (slug) => {
  return request(`/api/slug`, {
    method: methods.GET,
    params: {
      slug,
    },
  });
};

export const autoAcceptTxViaOperator = (body) => {
  return post('/api/accept-tx-via-operator', body);
};

// Organisation endpoints (v2, JSON — not Transit). The members and invitations
// management calls moved to src/v2/api/organisations.ts with the page (CH-527);
// these two have consumers outside it.
const jsonHeaders = { 'Content-Type': 'application/json' };

export const acceptOrganisationInvitation = ({ code }) => {
  return post('/api/v2/organisations/invitations/accept', { code }, { headers: jsonHeaders });
};

export const getMyMembership = ({ ssrCookie } = {}) => {
  const headers = ssrCookie ? { ...jsonHeaders, Cookie: ssrCookie } : jsonHeaders;
  return request('/api/v2/organisations/memberships/me', { method: methods.GET, headers });
};

// Fire a cheap request purely so the response interceptor sees the current
// X-App-Version header (see util/appVersion.js). Throttled because the health
// endpoint touches the database, and a browser fires visibilitychange on every
// tab switch. Returns the (never-rejecting) request promise so callers can
// hold the splash gate open while the check runs, or null when throttled.
const VERSION_PING_INTERVAL_MS = 5 * 60 * 1000;
let lastVersionPing = 0;

export const pingVersion = () => {
  if (Date.now() - lastVersionPing < VERSION_PING_INTERVAL_MS) return null;
  lastVersionPing = Date.now();
  return request('/api/v2/health', { method: methods.GET, headers: jsonHeaders }).catch(() => {});
};
